import { useState, useMemo, useEffect } from 'react';
import type { ChatHeight } from '@/lib/chat-customization';
import {
  PANEL_COMPACT_BORDER_RADIUS,
  getDefaultCompactPanelHeight,
  resolveCompactPanelHeight,
  clampPanelCompactHeight,
  getMaxCompactPanelHeight,
} from '@/lib/panel-resize';
import { DEFAULT_LAUNCHER_MY } from '@/lib/embed-style';

// The open panel stays at the regular bottom-right corner even when the embed
// moves the launcher with larger `mx`/`my` values (SEM-7379).
const COMPACT_PANEL_EDGE_INSET = DEFAULT_LAUNCHER_MY;

export type PanelHeightMode = 'compact' | 'full height';

export interface UseCompactPanelLayoutOptions {
  chatHeight: ChatHeight;
  isMobile: boolean;
  open: boolean;
  marginTop?: number;
  mobileViewportHeight: number | null;
  mobileViewportOffsetTop: number;
  windowHeight: number;
}

export interface CompactPanelLayout {
  height: string;
  top: string | number;
  bottom: string | number;
  right: string | number;
  borderRadius: number;
  showHeightResize: boolean;
  showExpandControl: boolean;
  isExpanded: boolean;
  heightMode: PanelHeightMode;
  expand: () => void;
  collapse: () => void;
  setCompactHeight: (height: number) => void;
  compactHeightPx: number;
  maxCompactHeight: number;
}

export function useCompactPanelLayout(
  options: UseCompactPanelLayoutOptions,
): CompactPanelLayout {
  const {
    chatHeight,
    isMobile,
    open,
    marginTop = 0,
    mobileViewportHeight,
    mobileViewportOffsetTop,
    windowHeight,
  } = options;

  const isCompactInstitution = chatHeight === 'compact' && !isMobile;
  // Bottom inset (COMPACT_PANEL_EDGE_INSET) is already excluded from available height; do not
  // subtract PANEL_VIEWPORT_VERTICAL_MARGIN again inside the helpers.
  const compactViewportHeight = Math.max(0, windowHeight - marginTop - COMPACT_PANEL_EDGE_INSET);
  const maxCompactHeight = getMaxCompactPanelHeight(compactViewportHeight, 0);
  const defaultCompactHeight = getDefaultCompactPanelHeight(compactViewportHeight);

  const [isExpanded, setIsExpanded] = useState(false);
  const [compactHeight, setCompactHeightState] = useState(defaultCompactHeight);

  useEffect(() => {
    if (open) return;
    queueMicrotask(() => {
      setIsExpanded(false);
      setCompactHeightState(getDefaultCompactPanelHeight(compactViewportHeight));
    });
  }, [open, compactViewportHeight]);

  const resetCompactDimensions = () => {
    setCompactHeightState(getDefaultCompactPanelHeight(compactViewportHeight));
  };

  const layout = useMemo(() => {
    if (isMobile) {
      if (mobileViewportHeight !== null) {
        return {
          height: `${mobileViewportHeight}px`,
          top: `${mobileViewportOffsetTop + marginTop}px`,
          bottom: 'auto' as const,
          right: 0,
          borderRadius: 0,
          showHeightResize: false,
          showExpandControl: false,
          isExpanded: false,
          heightMode: 'full height' as PanelHeightMode,
          compactPx: compactHeight,
          useCompactCollapsed: false,
        };
      }

      const top = marginTop > 0 ? `${marginTop}px` : 0;

      return {
        height: marginTop > 0 ? `calc(100dvh - ${marginTop}px)` : '100dvh',
        top,
        bottom: 'auto' as const,
        right: 0,
        borderRadius: 0,
        showHeightResize: false,
        showExpandControl: false,
        isExpanded: false,
        heightMode: 'full height' as PanelHeightMode,
        compactPx: compactHeight,
        useCompactCollapsed: false,
      };
    }

    const { height: compactPx, forceFullHeight } = resolveCompactPanelHeight({
      preferredHeight: compactHeight,
      viewportHeight: compactViewportHeight,
      verticalMargin: 0,
    });

    const useFullLayout = !isCompactInstitution || isExpanded || forceFullHeight;

    if (useFullLayout) {
      const height =
        marginTop > 0 ? `calc(100dvh - ${marginTop}px)` : '100dvh';
      const top = marginTop > 0 ? `${marginTop}px` : 0;

      return {
        height,
        top,
        bottom: 'auto' as const,
        right: 0,
        borderRadius: 0,
        showHeightResize: false,
        showExpandControl: isCompactInstitution && !forceFullHeight,
        isExpanded,
        heightMode: 'full height' as PanelHeightMode,
        compactPx,
        useCompactCollapsed: false,
      };
    }

    return {
      height: `${compactPx}px`,
      top: 'auto' as const,
      bottom: COMPACT_PANEL_EDGE_INSET,
      right: COMPACT_PANEL_EDGE_INSET,
      borderRadius: PANEL_COMPACT_BORDER_RADIUS,
      showHeightResize: true,
      showExpandControl: true,
      isExpanded: false,
      heightMode: 'compact' as PanelHeightMode,
      compactPx,
      useCompactCollapsed: true,
    };
  }, [
    isMobile,
    mobileViewportHeight,
    mobileViewportOffsetTop,
    windowHeight,
    marginTop,
    compactViewportHeight,
    compactHeight,
    isCompactInstitution,
    isExpanded,
  ]);

  const expand = () => {
    resetCompactDimensions();
    setIsExpanded(true);
  };

  const collapse = () => {
    setIsExpanded(false);
    resetCompactDimensions();
  };

  const setCompactHeight = (height: number) => {
    setCompactHeightState(clampPanelCompactHeight(height, maxCompactHeight));
  };

  return {
    height: layout.height,
    top: layout.top,
    bottom: layout.bottom,
    right: layout.right,
    borderRadius: layout.borderRadius,
    showHeightResize: layout.showHeightResize,
    showExpandControl: layout.showExpandControl,
    isExpanded: layout.isExpanded,
    heightMode: layout.heightMode,
    expand,
    collapse,
    setCompactHeight,
    compactHeightPx: layout.compactPx,
    maxCompactHeight,
  };
}
